import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
} from '@mui/material';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import { Edit, Add, Delete, Close, Save } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { headerSaga } from 'app/pages/Header/saga';
import { reducer, sliceKey } from 'app/pages/Header/slice';
import ModalMAIQ from 'app/components/ModalMAIQ';
import AddIcon from '@mui/icons-material/Add';
import DataGridMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import DataGridTypes from 'types/DataGridTypes';
import FooterImageDetailsModal from './FooterImageDetailsModal';
import FooterImageService from 'app/apiService/footerImage/footerImageApiService';
import ConfirmationModal from 'app/components/ConfirmationModal';
import FooterImageForm, { FooterImageFormData, FooterImageFormErrors } from './components/FooterImageForm';

// Modal for Add/Edit Footer Image
const FooterImageModal = ({ open, onClose, initialData, onSave, isSubmitting }: any) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<FooterImageFormData>({
    image_description: '',
    image_address: '',
    image_url: '',
    expiry_date: '',
  });
  const [formErrors, setFormErrors] = useState<FooterImageFormErrors>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        image_description: initialData.image_description || '',
        image_address: initialData.image_address || '',
        image_url: initialData.image_url || '',
        expiry_date: initialData.expiry_date || '',
      });
    } else {
      setFormData({
        image_description: '',
        image_address: '',
        image_url: '',
        expiry_date: '',
      });
    }
    setFormErrors({});
  }, [initialData, open]);

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific error when user starts typing
    if (formErrors[field as keyof FooterImageFormErrors]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof FooterImageFormErrors];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: FooterImageFormErrors = {};

    if (!formData.image_description?.trim()) {
      errors.image_description = 'Image description is required';
    }
    if (!formData.image_address?.trim()) {
      errors.image_address = 'Image address is required';
    }
    if (!formData.image_url?.trim()) {
      errors.image_url = 'Landing page URL is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSave(formData);
  };

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{
          width: 600,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          className="modal-header"
          sx={{
            p: 2,
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon className="modal-header-icon" fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                {initialData ? 'Edit Footer Image' : 'Add New Footer Image'}
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  className="modal-header-button"
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    p: 0.5,
                  }}
                  size="small"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Scrollable content area */}
        <Box
          component="form"
          id="footer-image-form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            py: 2,
            px: 3
          }}
        >
          {Object.keys(formErrors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Please fix the errors below
            </Alert>
          )}
          <FooterImageForm
            formData={formData}
            onFormChange={handleFormChange}
            errors={formErrors}
            disabled={isSubmitting}
            showImagePreview={true}
          />
        </Box>

        {/* Footer action bar */}
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'flex-end',
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            zIndex: 9,
            flexShrink: 0,
            gap: 2
          }}
        >
          <ButtonMAIQ
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </ButtonMAIQ>
          {isSubmitting ? (
            <Skeleton
              variant="rectangular"
              width={140}
              height={36}
              sx={{
                borderRadius: 1,
                bgcolor: theme.palette.action.hover,
              }}
            />
          ) : (
            <ButtonMAIQ
              variant="contained"
              color="primary"
              type="submit"
              form="footer-image-form"
              onClick={(e) => {
                // If form submission doesn't happen through the form's onSubmit,
                // trigger it manually here as a fallback
                handleSubmit(e as any);
              }}
              startIcon={<Save />}
            >
              {initialData ? 'Update Image' : 'Create Image'}
            </ButtonMAIQ>
          )}
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

const FooterImageTab = () => {
  const theme = useTheme();
  const location = useLocation();
  const selectedDealer = useSelector(selectDealerSelected) || {};
  
  // All hooks must be called before any early returns (React Hooks rule)
  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteImageId, setDeleteImageId] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] = useState<any>(null);
  const [gridDeleteConfirm, setGridDeleteConfirm] = useState(false);

  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });
  
  // Check if we're in NewAIAvatarLayout context
  const isInAIAvatarLayout = location.pathname.startsWith('/ai-avatar-layout');
  const selectedDealerId = selectedDealer.dealer || '';

  // Check if dealer is selected - if not, show message
  if (!selectedDealerId) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          color: 'text.primary',
          fontSize: '1.5rem',
          fontWeight: 500,
        }}
      >
        <Typography variant="h4" gutterBottom component="div">
          Please select a dealer to view footer images
        </Typography>
      </Box>
    );
  }

  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (row: any) => {
    setEditData(row);
    setModalOpen(true);
  };

  const handleViewDetails = (row: any) => {
    setSelectedImage(row);
    setDetailModalOpen(true);
  };

  const handleDelete = async (row: any) => {
    setSelectedRowToDelete(row);
    setGridDeleteConfirm(true);
  };

  const confirmGridDelete = async () => {
    if (!selectedRowToDelete) return;
    setIsDeleting(true);
    try {
      await FooterImageService.deleteFooterImage(selectedRowToDelete._id);
      setRefreshKey(prev => prev + 1);
      setGridDeleteConfirm(false);
      setSelectedRowToDelete(null);
    } catch (err) {
      console.error('Delete footer image error', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const saveImage = async (id: string | null, payload: any) => {
    try {
      if (id) {
        await FooterImageService.editFooterImage(id, payload);
      } else {
        await FooterImageService.addFooterImage(payload);
      }
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Save footer image error', err);
    }
  };

  const handleSave = async (data: any) => {
    setIsSubmitting(true);
    try {
      const payload = { ...data, dealership_id: selectedDealerId };
      await saveImage(editData?._id || null, payload);
      setModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCellClick = (cell: any, clickedElement?: string) => {
    if (cell.field === 'actions') {
      // Handle action buttons (edit, delete)
      if (clickedElement === 'delete') {
        handleDelete(cell.row);
      } else if (clickedElement === 'edit') {
        handleEdit(cell.row);
      }
    } else if (cell.field === 'image_description') {
      // Handle image description click - open detail modal
      handleViewDetails(cell.row);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              Footer Secondary Offer Images
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              Manage your email footer images and primary offers
            </Typography>
          </Box>
          <ButtonMAIQ
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={isInAIAvatarLayout ? {
              backgroundColor: theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white,
              color: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.common.black,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[800] : theme.palette.grey[200],
              },
            } : undefined}
          >
            Add New
          </ButtonMAIQ>
        </Box>
        <DataGridMAIQ
          type={DataGridTypes.FOOTER_IMAGE(selectedDealerId)}
          onCellClick={handleCellClick}
          key={refreshKey}
        />
      </Paper>
      <FooterImageModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialData={editData}
        onSave={handleSave}
        isSubmitting={isSubmitting}
      />
      <FooterImageDetailsModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        data={selectedImage}
        onSave={async payload => {
          await saveImage(selectedImage?._id, { ...payload, dealership_id: selectedDealerId });
          setDetailModalOpen(false);
          setRefreshKey(prev => prev + 1);
        }}
        onDelete={async () => {
          try {
            await FooterImageService.deleteFooterImage(selectedImage._id);
            setDetailModalOpen(false);
            setRefreshKey(prev => prev + 1);
          } catch (err) {
            console.error('Delete footer image error', err);
          }
        }}
      />
      <ConfirmationModal
        open={gridDeleteConfirm}
        onClose={() => setGridDeleteConfirm(false)}
        onConfirm={confirmGridDelete}
        message="Are you sure you want to delete this footer image?"
        loading={isDeleting}
      />
    </Box>
  );
};

export default FooterImageTab;
